import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PrintableOrderAppendix from '../printable/PrintableOrderAppendix';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useSettings } from '../../contexts/SettingsContext'; // Import useSettings hook

// Re-define interfaces needed for this page, matching the JSON structure
// Ideally, these would be in a shared types file.
interface Structure {
  id: number;
  code: string;
  name?: string;
  type: string;
  location: string;
  description?: string;
  isLED?: boolean;
}
interface OrderItem {
  id: number;
  orderId: number;
  structureId: number;
  Structure: Structure; // Nested object name from JSON
  startDate: string | Date;
  endDate: string | Date;
  price?: number; // Keep for calculation fallback?
  unitPrice: number; // Use unitPrice from JSON
  quantity: number;
  isMonthlyRate: boolean;
  discountPercentage: number | null;
  totalPrice: number;
  finalPrice: number;
  notes?: string;
  status?: string;
}
interface Client {
  id: number;
  name: string;
  director?: string; // Use director from JSON
  phone?: string;
  email?: string;
  address?: string;
  bin?: string; // Not in provided Client JSON, but used in template
  iik?: string; // Not in provided Client JSON, but used in template
  taxId?: string; // Available in JSON
}
interface Contract {
  _id?: string;
  id?: number;
  contractNumber?: string;
  contractDate?: string | Date;
}
interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  Client: Client; // Nested object name from JSON - Mark as required
  status: string;
  totalAmount: number;
  discountAmount: number;
  finalAmount: number;
  paidAmount: number;
  orderDate: string | Date;
  startDate: string | Date;
  endDate: string | Date;
  paymentTerms?: string;
  notes?: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  contractId?: number | null; // Keep as potentially populated
  contract?: Contract; // Keep as potentially populated
  items?: OrderItem[]; // Keep for compatibility if needed
  OrderItems: OrderItem[]; // Use OrderItems from JSON - Mark as required
}
// Settings interface is handled by useSettings hook

const OrderAppendixPrintPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [order, setOrder] = useState<Order | null>(null);
    const [loadingOrder, setLoadingOrder] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { settings: companySettings, loading: loadingSettings, error: settingsError } = useSettings(); // Use context for settings
    const printTriggered = useRef(false); // Ref to ensure print is called only once

    useEffect(() => {
        const fetchOrder = async () => {
            setLoadingOrder(true);
            setError(null);
            try {
                const response = await axios.get<Order>(`/orders/${id}`);
                // No need to map items anymore, PrintableOrderAppendix uses OrderItems
                setOrder(response.data);
            } catch (err: any) {
                console.error('Error fetching order for printing appendix:', err);
                setError(err.response?.data?.message || 'Не удалось загрузить данные заказа для печати приложения.');
            } finally {
                setLoadingOrder(false);
            }
        };

        if (id) {
            fetchOrder();
        } else {
            setError('ID заказа не указан.');
            setLoadingOrder(false);
        }
    }, [id]);

    useEffect(() => {
        // Trigger print dialog once data is loaded and print hasn't been triggered yet
        if (!loadingOrder && !loadingSettings && order && companySettings && !printTriggered.current) {
            printTriggered.current = true; // Mark print as triggered
            // Delay print slightly to ensure content is fully rendered
            setTimeout(() => {
                // window.print();
            }, 500); // Adjust delay if needed
        }
    }, [loadingOrder, loadingSettings, order, companySettings]);

    if (loadingOrder || loadingSettings) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><LoadingSpinner /></div>;
    }

    if (error || settingsError) {
        return <div style={{ padding: '20px', color: 'red', textAlign: 'center' }}>Ошибка: {error || settingsError}</div>;
    }

    if (!order || !companySettings) {
        return <div style={{ padding: '20px', textAlign: 'center' }}>Не удалось загрузить данные для печати.</div>;
    }

    // Pass the correct settings object (it's already named 'settings' in the context, but the prop expects 'companySettings')
    return <PrintableOrderAppendix order={order} companySettings={companySettings} />;
};

export default OrderAppendixPrintPage;
